import { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { Search, X, Sparkles, BookOpen, Landmark } from 'lucide-react';

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (query: string) => void;
  isSearching: boolean;
}

const recentSearches = ['minimum wage history', 'equal pay act 1963', 'labor force participation'];

const trendingSearches = [
  'inflation adjustment',
  'federal register notices',
  'congressional hearings',
];

export function SearchOverlay({ isOpen, onClose, onSearch, isSearching }: SearchOverlayProps) {
  const [query, setQuery] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Animate in
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3, ease: 'power2.out' }
      );
      gsap.fromTo(
        contentRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.4, delay: 0.1, ease: 'power2.out' }
      );
      // Focus input
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  const handleClose = () => {
    gsap.to(contentRef.current, {
      y: 30,
      opacity: 0,
      duration: 0.2,
      ease: 'power2.in',
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.3,
      delay: 0.1,
      ease: 'power2.in',
      onComplete: onClose,
    });
  };

  const runSearch = (term: string) => {
    const normalizedQuery = term.trim();
    if (!normalizedQuery) return;

    setQuery(normalizedQuery);
    onSearch(normalizedQuery);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(inputRef.current?.value ?? query);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[200] flex items-start justify-center bg-[#0B0D10]/90 pt-[20vh] backdrop-blur-xl"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div ref={contentRef} className="mx-4 w-full max-w-2xl">
        {/* Search Form */}
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative flex items-center">
            <Search className="absolute left-6 h-6 w-6 text-[#6B6F78]" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search a claim or topic..."
              className="w-full rounded-2xl bg-white py-5 pl-16 pr-40 text-xl text-[#0B0D10] transition-shadow placeholder:text-[#9CA3AF] focus:outline-none focus:ring-4 focus:ring-[#E1062C]/30"
              disabled={isSearching}
            />
            <div className="absolute right-4 flex items-center gap-2">
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="rounded-xl bg-[#F6F5F2] p-3 text-[#6B6F78] transition-colors hover:bg-[#E1062C]/10 hover:text-[#E1062C]"
                  aria-label="Clear search"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => runSearch(inputRef.current?.value ?? query)}
                disabled={!query.trim() || isSearching}
                className="rounded-xl bg-[#E1062C] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#c00525] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Search
              </button>
            </div>
          </div>

          {isSearching && (
            <div className="mt-4 flex items-center justify-center gap-3 text-white">
              <Sparkles className="h-5 w-5 animate-spin text-[#E1062C]" />
              <span className="text-sm font-medium">Searching primary sources...</span>
            </div>
          )}
        </form>

        {/* Quick Searches */}
        {!isSearching && (
          <div className="mt-8 grid grid-cols-2 gap-6">
            {/* Recent */}
            <div>
              <div className="mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-[#6B6F78]" />
                <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                  Suggested topics
                </span>
              </div>
              <div className="space-y-2">
                {recentSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => runSearch(term)}
                    className="block w-full rounded-xl bg-white/5 px-4 py-3 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>

            {/* Trending */}
            <div>
              <div className="mb-3 flex items-center gap-2">
                <Landmark className="h-4 w-4 text-[#E1062C]" />
                <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                  More ideas
                </span>
              </div>
              <div className="space-y-2">
                {trendingSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => runSearch(term)}
                    className="block w-full rounded-xl bg-white/5 px-4 py-3 text-left text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-8 top-8 rounded-full bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
          aria-label="Close search"
        >
          <X className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}
